import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useColors } from "../constants/Colors";

interface FlatData {
  flat_id: number;
  name: string;
  location?: {
    display_name?: string;
    currency?: string;
  };
  member_count?: number;
  size?: number;
  room?: Array<{
    rent?: number;
    utilities?: number;
    deposit?: number;
    size?: number;
    furnishing?: string;
    permanent?: boolean;
    from_date?: string;
    to_date?: string;
  }>;
  flat_text?: string;
}

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  flat: FlatData;
}

type MessageLanguage = "en" | "de";

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, flat }) => {
  const { t } = useTranslation();
  const colors = useColors();
  const [messageLanguage, setMessageLanguage] = useState<MessageLanguage>("en");
  const [isEditing, setIsEditing] = useState(false);
  const [copied, setCopied] = useState(false);

  const flatUrl = `https://flateroo.com/app/flat-view/${flat.flat_id}`;
  const room = flat.room?.[0];
  const currency = flat.location?.currency || "CHF";

  const translations: Record<MessageLanguage, Record<string, string>> = {
    en: {
      month: "month",
      rent: "Rent",
      utilities: "Utilities",
      location: "Location",
      flatmates: "Flatmates",
      flatSize: "Flat size",
      roomSize: "Room size",
      deposit: "Deposit",
      furnishing: "Furnishing",
      stayType: "Stay type",
      availableFrom: "Available from",
      description: "Description",
      permanent: "Permanent",
      temporary: "Temporary",
      unfurnished: "Unfurnished",
      partly_furnished: "Partly furnished",
      furnished: "Furnished",
      roomAvailable: "Room available",
    },
    de: {
      month: "Monat",
      rent: "Miete",
      utilities: "Nebenkosten",
      location: "Standort",
      flatmates: "Mitbewohner",
      flatSize: "Wohnungsgröße",
      roomSize: "Zimmergröße",
      deposit: "Kaution",
      furnishing: "Möblierung",
      stayType: "Aufenthaltsart",
      availableFrom: "Verfügbar ab",
      description: "Beschreibung",
      permanent: "Dauerhaft",
      temporary: "Befristet",
      unfurnished: "Unmöbliert",
      partly_furnished: "Teilmöbliert",
      furnished: "Möbliert",
      roomAvailable: "Zimmer frei",
    },
  };

  const tr = translations[messageLanguage];

  const getFurnishingText = (furnishing?: string) => {
    if (!furnishing) return "";
    return tr[furnishing] || furnishing;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(
      messageLanguage === "de" ? "de-DE" : "en-US",
      {
        year: "numeric",
        month: "long",
        day: "numeric",
      },
    );
  };

  const generatedMessage = useMemo(() => {
    const lines = [
      flatUrl,
      "",
      `🏠 ${tr.roomAvailable}:`,
      `- 💰 ${tr.rent}: ${room?.rent || 0} ${currency}/${tr.month}`,
      `- 🔌 ${tr.utilities}: ${room?.utilities || 0} ${currency}/${tr.month}`,
      `- 📍 ${tr.location}: ${flat.location?.display_name || ""}`,
      `- 👥 ${tr.flatmates}: ${flat.member_count || 0}`,
    ];

    if (flat.size && flat.size > 0) {
      lines.push(`- 📐 ${tr.flatSize}: ${flat.size} m²`);
    }

    lines.push(
      `- 🛏️ ${tr.roomSize}: ${room?.size || 0} m²`,
      `- 💼 ${tr.deposit}: ${room?.deposit || 0} ${currency}`,
      `- 🛋️ ${tr.furnishing}: ${getFurnishingText(room?.furnishing)}`,
      `- 🔑 ${tr.stayType}: ${room?.permanent ? tr.permanent : tr.temporary}`,
      `- 📅 ${tr.availableFrom}: ${formatDate(room?.from_date)}`,
    );

    if (!room?.permanent && room?.to_date) {
      lines[lines.length - 1] += ` - ${formatDate(room.to_date)}`;
    }

    if (flat.flat_text) {
      lines.push("", `${tr.description}: ${flat.flat_text}`);
    }

    return lines.join("\n");
  }, [flat, room, currency, messageLanguage, tr]);

  const [editedMessage, setEditedMessage] = useState(generatedMessage);
  const [hasCustomMessage, setHasCustomMessage] = useState(false);

  React.useEffect(() => {
    if (!hasCustomMessage) {
      setEditedMessage(generatedMessage);
    }
  }, [generatedMessage, hasCustomMessage]);

  const currentMessage = hasCustomMessage ? editedMessage : generatedMessage;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: flat.name,
          text: currentMessage,
        });
      } catch (err) {
        console.error("Share failed:", err);
      }
    } else {
      handleCopy();
    }
  };

  const toggleLanguage = () => {
    setMessageLanguage((prev) => (prev === "en" ? "de" : "en"));
    setHasCustomMessage(false);
  };

  const handleEditToggle = () => {
    if (isEditing) {
      if (editedMessage !== generatedMessage) {
        setHasCustomMessage(true);
      }
      setIsEditing(false);
    } else {
      setEditedMessage(currentMessage);
      setIsEditing(true);
    }
  };

  const handleResetMessage = () => {
    setEditedMessage(generatedMessage);
    setHasCustomMessage(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        onClick={onClose}
        style={{
          backgroundColor: "rgba(2, 2, 16, 0.45)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-lg max-h-[85vh] flex flex-col"
        style={{
          backgroundColor: colors.containerNeutralSolidSurface,
          borderRadius: 28,
          boxShadow:
            "0 24px 80px rgba(2, 2, 16, 0.18), 0 0 0 1px rgba(2, 2, 16, 0.04)",
          animation:
            "scaleIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
          <div className="w-9 h-9 flex-shrink-0" />
          <h2
            className="text-xl font-gnarly-semibold tracking-tight m-0 text-center flex-1"
            style={{ color: colors.textNeutralDefault }}
          >
            {t("share") || "Share"}
          </h2>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 hover:scale-105"
            style={{ backgroundColor: "rgba(2, 2, 16, 0.05)" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: colors.textNeutralMuted }}
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 min-h-0">
          {/* Language toggle */}
          <div className="flex items-center justify-between mb-4">
            <span
              className="text-sm"
              style={{ color: colors.textNeutralMuted }}
            >
              {t("share_modal.message_language") || "Message language"}
            </span>
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl transition-all duration-150 hover:scale-[1.02] active:scale-[0.98]"
              style={{
                backgroundColor: "rgba(91, 61, 246, 0.06)",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: colors.accentColor }}
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <span
                className="text-sm font-medium"
                style={{ color: colors.accentColor }}
              >
                {messageLanguage === "en" ? "English" : "Deutsch"}
              </span>
            </button>
          </div>

          {/* Message preview/edit */}
          <div
            className="rounded-2xl p-4 mb-5"
            style={{ backgroundColor: colors.containerNeutralLightLowest }}
          >
            {isEditing ? (
              <textarea
                value={editedMessage}
                onChange={(e) => setEditedMessage(e.target.value)}
                className="w-full h-64 bg-transparent resize-none outline-none text-sm leading-relaxed"
                style={{ color: colors.textNeutralDefault }}
              />
            ) : (
              <pre
                className="whitespace-pre-wrap text-sm leading-relaxed overflow-x-auto m-0"
                style={{
                  color: colors.textNeutralDefault,
                  fontFamily: "inherit",
                }}
              >
                {currentMessage}
              </pre>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2.5">
            {/* Edit / Save */}
            <button
              onClick={handleEditToggle}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full transition-all duration-150 hover:scale-[1.02] active:scale-[0.98]"
              style={{
                backgroundColor: "rgba(2, 2, 16, 0.05)",
                color: colors.textNeutralDefault,
              }}
            >
              {isEditing ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
              )}
              <span className="text-sm font-medium">
                {isEditing ? t("save") || "Save" : t("edit") || "Edit"}
              </span>
            </button>

            {/* Reset (editing) / Copy (viewing) */}
            {isEditing ? (
              <button
                onClick={handleResetMessage}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full transition-all duration-150 hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  backgroundColor: "rgba(2, 2, 16, 0.05)",
                  color: colors.textNeutralMuted,
                }}
              >
                <span className="text-sm font-medium">
                  {t("reset") || "Reset"}
                </span>
              </button>
            ) : (
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full transition-all duration-150 hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  backgroundColor: copied
                    ? "rgba(34, 197, 94, 0.1)"
                    : "rgba(2, 2, 16, 0.05)",
                  color: copied ? "#16a34a" : colors.textNeutralDefault,
                }}
              >
                {copied ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                )}
                <span className="text-sm font-medium">
                  {copied
                    ? t("copied") || "Copied!"
                    : t("share_modal.copy") || "Copy"}
                </span>
              </button>
            )}

            {/* Share button */}
            {typeof navigator !== "undefined" &&
              typeof navigator.share === "function" && (
                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full font-gnarly-semibold transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] ml-auto"
                  style={{
                    backgroundColor: colors.accentColor,
                    color: colors.textNeutralOnPrimary,
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></svg>
                  <span className="text-sm">
                    {t("share") || "Share"}
                  </span>
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
